import outline from '@/assets/100000.json'
import { mapPresentation, mapTint } from './presentation'

// 附图只是全国边界的另一处静态取景，不是第二个可漫游坐标系，也不承载设备数据。
const bounds = Object.freeze([105, 2, 124.5, 25.5])
const mainlandLatitude = 18
const mainlandMaps = new WeakMap()
const mapExtents = new WeakMap()
const project = point => [
  ((point[0] - bounds[0]) / (bounds[2] - bounds[0]) * 112).toFixed(2),
  ((bounds[3] - point[1]) / (bounds[3] - bounds[1]) * 140).toFixed(2)
]

function polygonPath (rings) {
  return rings.map(ring => ring.map((point, index) => (index ? 'L' : 'M') + project(point).join(',')).join('') + 'Z').join('')
}

// 原始闭合轮廓不裁点、不挪动；仅由 SVG 视口裁剪越出取景框的部分，保留岛屿及附属轮廓。
export const southChinaSeaPath = outline.features.map(feature => {
  const polygons = feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates
  return polygons.map(polygonPath).join('')
}).join('')

export function supportsSouthChinaSeaInset (option = {}) {
  if (Number(option.type || 0) !== 0) return false
  if (option.regionCode) return String(option.regionCode) === '100000'
  const value = option.mapData && option.mapData.value
  if (!value) return true
  if (value.type !== 'FeatureCollection' || !Array.isArray(value.features)) return false
  if (value.features.some(feature => String(feature.properties && feature.properties.adcode) === '100000')) return true
  // 兼容旧全国省／市级 GeoJSON；不能仅因旧配置没有 regionCode 就把省图当成全国。
  const regions = new Set(value.features.map(feature => String(feature.properties && feature.properties.adcode).slice(0, 2)))
  return ['11', '46', '65', '71'].every(code => regions.has(code))
}

// 仅为附图开启时的全国主图派生展示几何，不改原边界和统计区域。
// 保留海南、台湾及北侧完整离散面；南海岛礁在附图中展示，不再拉长主图。
export function mainMapGeometry (geo, option = {}, rows = []) {
  if (option.southChinaSeaInset !== true || !supportsSouthChinaSeaInset(option) ||
      rows.some(row => Number.isFinite(row.lat) && row.lat < mainlandLatitude)) return geo
  if (mainlandMaps.has(geo)) return mainlandMaps.get(geo)
  const features = (geo.features || []).map(feature => {
    const geometry = feature.geometry || {}
    if (!['Polygon', 'MultiPolygon'].includes(geometry.type)) return feature
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
    const visible = polygons.filter(rings => rings[0].some(point => point[1] >= mainlandLatitude))
    if (!visible.length) return null
    if (visible.length === polygons.length) return feature
    return { ...feature, geometry: { ...geometry, coordinates: geometry.type === 'Polygon' ? visible[0] : visible } }
  }).filter(Boolean)
  const result = { ...geo, features }
  mainlandMaps.set(geo, result)
  return result
}

export function mainMapFrame (geo, project) {
  if (typeof project !== 'function') return undefined
  if (!mapExtents.has(geo)) {
    const extent = [Infinity, Infinity, -Infinity, -Infinity]
    for (const feature of geo.features || []) {
      const geometry = feature.geometry || {}
      const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.type === 'MultiPolygon' ? geometry.coordinates : []
      polygons.forEach(rings => rings[0].forEach(([lng, lat]) => {
        extent[0] = Math.min(extent[0], lng); extent[1] = Math.min(extent[1], lat)
        extent[2] = Math.max(extent[2], lng); extent[3] = Math.max(extent[3], lat)
      }))
    }
    mapExtents.set(geo, extent)
  }
  const [west, south, east, north] = mapExtents.get(geo)
  const first = project([west, north]), last = project([east, south])
  if (![first, last].every(point => Array.isArray(point) && point.length === 2 && point.every(Number.isFinite))) return undefined
  return { left: Math.min(first[0], last[0]), top: Math.min(first[1], last[1]), right: Math.max(first[0], last[0]), bottom: Math.max(first[1], last[1]) }
}

export function southChinaSeaInset (option = {}, width = 800, height = 500, frame) {
  const visual = mapPresentation(option)
  const w = Math.max(1, parseFloat(width) || 800), h = Math.max(1, parseFloat(height) || 500)
  const insetWidth = Math.max(1, Math.min(w - 4, h * .48, Math.max(24, Math.min(110, w * .17, h * .26))))
  const position = (value, available) => Math.max(0, Math.min(Math.max(0, available - 4), Math.max(4, value)))
  return {
    show: option.southChinaSeaInset === true && supportsSouthChinaSeaInset(option),
    path: southChinaSeaPath,
    color: mapTint(visual.borderColor), fill: mapTint(visual.areaColor),
    background: mapTint(visual.areaColor, -.65, .96),
    width: insetWidth + 'px',
    right: (frame ? position(w - frame.right, w - insetWidth) : Math.min(14, w * .025)) + 'px',
    bottom: (frame ? position(h - frame.bottom, h - insetWidth * 160 / 120) : Math.min(14, h * .025)) + 'px'
  }
}
